/**
 * What a mint's answer must hold to be a session: the rule {@link SessionTokenEngine} reads every
 * answer through before it commits one.
 *
 * The mint is the application's own callback over its own endpoint, so its answer is typed as a
 * `Session` and is still whatever that endpoint sent: the JSON body of a 500 read without a look
 * at the status, a proxy's page, `null`. An answer committed unread leaves a surface with no
 * token and no error, waiting on neither.
 */

import { describeType, MountayaConnectionError } from "./errors.js";

/** SessionAnswer is a mint's answer once it is shown to be a session. */
export interface SessionAnswer {
  /**
   * When the token expires, or undefined where the answer's `expires_at` names no instant: the
   * token is usable and only its lifetime is unknown.
   */
  readonly expiresAt: Date | undefined;
  /** The session token, a string that is not empty. */
  readonly token: string;
  /**
   * The user the token was minted for: the answer's `user_id`, `null` where the token names no
   * user, and undefined where the answer does not say, which an endpoint that forwards only
   * `{ token, expires_at }` does.
   */
  readonly userId: string | null | undefined;
}

/** What every refusal ends with: the shape to answer, and how an endpoint's failure is told. */
const REMEDY =
  "Answer the { token, expires_at, user_id } that POST /v1/sessions returns, and reject where your endpoint fails, as a mint that checks the response's status does.";

/**
 * isDate tells a `Date` by its tag rather than by its constructor, so one built in another realm
 * (a frame, another context) is read as the instant it is.
 */
const isDate = (value: unknown): value is Date =>
  Object.prototype.toString.call(value) === "[object Date]";

/**
 * readSessionAnswer reads a mint's answer as a session, refusing one that holds no token or
 * nothing to read an expiry from.
 *
 * `expires_at` is a `Date` from the REST surface, a string once the application's own endpoint
 * has put it through JSON, and a number where that endpoint writes its instants as milliseconds
 * since the epoch: each is read as `new Date` reads it. One that is present and names no instant
 * keeps its token: the token is usable and only its lifetime is unknown, so the engine re-mints
 * it on the flat margin. One that is absent, or none of the three, is an answer that is no
 * session at all.
 *
 * `user_id` is read as the user the token was minted for: a string names one, `null` names none,
 * and anything else, its absence included, says nothing, so a call that acts as a member is sent
 * for the API to decide rather than refused for a user the answer never stated.
 *
 * The refusal names each member by its type and never by its value, which may be a key.
 *
 * @throws `MountayaConnectionError` for an answer that is not an object, whose `token` is not a
 *   string or is empty, or whose `expires_at` is not a string, a number or a `Date`.
 */
export const readSessionAnswer = (answer: unknown): SessionAnswer => {
  if (typeof answer !== "object" || answer === null) {
    throw new MountayaConnectionError(
      `mint: the answer is ${describeType(answer)}, so it is no session. ${REMEDY}`,
    );
  }

  const token: unknown = Reflect.get(answer, "token");
  if (typeof token !== "string" || token === "") {
    throw new MountayaConnectionError(
      `mint: the answer holds no token (token is ${token === "" ? "an empty string" : describeType(token)}), so it is no session. ${REMEDY}`,
    );
  }

  const expiry: unknown = Reflect.get(answer, "expires_at");
  if (typeof expiry !== "string" && typeof expiry !== "number" && !isDate(expiry)) {
    throw new MountayaConnectionError(
      `mint: the answer holds no expiry to read (expires_at is ${describeType(expiry)}), so it is no session. ${REMEDY}`,
    );
  }

  const parsed = new Date(expiry);
  const user: unknown = Reflect.get(answer, "user_id");

  return {
    expiresAt: Number.isNaN(parsed.getTime()) ? undefined : parsed,
    token,
    userId: typeof user === "string" && user !== "" ? user : user === null ? null : undefined,
  };
};
